import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { combineLatest, forkJoin, Observable, Subject, tap } from 'rxjs';
import { AuditDetails, Auth0Connection } from '@app/shared/models/audit-details.interface';
import { ToastService } from '@services/toast/toast.service';
import { map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatHint, MatInput, MatLabel } from '@angular/material/input';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { MatDivider } from '@angular/material/divider';
import { AuditResource } from '@app/shared/resources/audit.resource';
import { NotificationBlockComponent, StatusClass } from '@components/notification-block/notification-block.component';
import { IsAuthorizedDirective } from '@app/shared/directives/is-authorized.directive';
import { GlobalAuthority } from '@app/shared/models/global-authority';
import { SecurityService } from '@services/security.service';

@Component({
  selector: 'app-integrations-siem',
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    MatHint,
    MatDivider,
    NotificationBlockComponent,
    IsAuthorizedDirective
  ],
  templateUrl: './siem.component.html',
  styleUrl: './siem.component.scss'
})
export class SiemComponent implements OnInit, OnDestroy {
  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private toaster: ToastService,
    private auditResource: AuditResource,
    private securityService: SecurityService
  ) {}

  buildingId: number;
  fg: UntypedFormGroup;
  details$: Observable<AuditDetails>;

  connectionsForm: FormGroup;
  availableConnections: Auth0Connection[] = [];
  savingConnections = false;
  private initialConnections: string[] = [];

  public readonly error$ = new Subject<string>();
  public readonly submit$ = new Subject<void>();
  public readonly reset$ = new Subject<void>();
  public readonly onDestroy$ = new Subject<void>();

  protected readonly _maxLength = 255;
  protected readonly GlobalAuthority = GlobalAuthority;

  private defaultAudit: AuditDetails;

  ngOnInit(): void {
    this.connectionsForm = this.fb.group({ connections: [[]] });

    this.route.params
      .pipe(
        map((params) => {
          this.buildingId = parseInt(params.buildingId);
          this.fg = this.initFormGroup();
          this.defaultAudit = {
            logAuthorization: 'AUTHORIZATION_HEADER',
            logFormat: 'CEF',
            syslog: true,
            buildingId: this.buildingId
          };
          this.fg.reset(this.defaultAudit);
          this.details$ = this.auditResource.getAuditDetailsForBuilding(this.buildingId).pipe(
            tap((details) => {
              if (details != null && details.buildingId === this.buildingId) {
                this.fg.reset(details);
              }
            })
          );
          this.securityService.isAuthorized(GlobalAuthority.MANAGE_BUILDINGS).subscribe((isAuth) => {
            if (isAuth) {
              this.loadConnections();
            }
          });
        })
      )
      .subscribe();

    this.initReset();
    this.initSubmit();
    this.initError();
  }

  private loadConnections(): void {
    forkJoin({
      available: this.auditResource.getAvailableConnections(),
      current: this.auditResource.getBuildingConnections(this.buildingId)
    }).subscribe({
      next: ({ available, current }) => {
        this.availableConnections = available;
        this.initialConnections = current.map((connection) => connection.connectionName);
        this.connectionsForm.reset({ connections: this.initialConnections });
      },
      error: () => {
        this.toaster.error({ message: 'Error loading connections', dataCy: 'connections-load-toast-error' });
      }
    });
  }

  submitConnections(): void {
    const selectedNames: string[] = this.connectionsForm.get('connections').value ?? [];
    this.savingConnections = true;
    this.auditResource.updateBuildingConnections(this.buildingId, selectedNames).subscribe({
      next: () => {
        this.toaster.success({ message: 'Connections Saved!', dataCy: 'connections-toast-success' });
        this.initialConnections = selectedNames;
        this.savingConnections = false;
        this.connectionsForm.markAsPristine();
      },
      error: () => {
        this.toaster.error({ message: 'Error when saving Connections', dataCy: 'connections-toast-error' });
        this.savingConnections = false;
      }
    });
  }

  resetConnections(): void {
    this.connectionsForm.reset({ connections: this.initialConnections });
  }

  private readonly httpsPattern = /^https:\/\/([^:/?#\s]+)+(:\d+)?(\/[^?#\s]+)?(\?[^#\s]+)?$/;

  private initFormGroup(): FormGroup {
    return this.fb.group({
      buildingId: [{ value: this.buildingId, disabled: true, hidden: true }],
      uri: [
        { value: null },
        [Validators.required, Validators.maxLength(this._maxLength), Validators.pattern(this.httpsPattern)]
      ],
      clientId: [{ value: null }, [Validators.maxLength(this._maxLength)]],
      clientSecret: [{ value: null }, [Validators.required, Validators.maxLength(this._maxLength)]],
      syslog: [{ value: null }],
      scope: [{ value: null }, [Validators.maxLength(this._maxLength)]],
      authenticationUrl: [
        { value: null },
        [Validators.maxLength(this._maxLength), Validators.pattern(this.httpsPattern)]
      ],
      logFormat: [{ value: null }, Validators.required],
      logAuthorization: [{ value: null }, Validators.required]
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public submit(): void {
    this.submit$.next();
  }

  private initSubmit(): void {
    this.submit$.pipe(withLatestFrom(this.details$), takeUntil(this.onDestroy$)).subscribe({
      next: ([, auditDetails]) => {
        const updatedAuditDetails = {
          ...auditDetails,
          ...this.fg.value
        };
        if (updatedAuditDetails.logFormat !== 'CEF') {
          updatedAuditDetails.syslog = false;
        }
        updatedAuditDetails.buildingId = this.buildingId;
        this.processSubmit(updatedAuditDetails);
      },
      error: (err) => {
        console.log(err);
        this.error$.next(err);
      }
    });
  }

  processSubmit(auditDetails: AuditDetails): void {
    this.auditResource.save(auditDetails).subscribe({
      next: () => {
        this.toaster.success({ message: 'SIEM Details Saved!', dataCy: 'siem-toast-success' });
        this.fg.markAsPristine();
      },
      error: () => {
        this.toaster.error({ message: 'Error when saving SIEM Details', dataCy: 'siem-toast-error' });
      }
    });
  }

  public reset(): void {
    this.reset$.next();
  }

  private initReset(): void {
    combineLatest([this.details$, this.reset$])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: ([auditDetails]) => {
          if (auditDetails != null && auditDetails.buildingId === this.buildingId) {
            this.fg.reset({ ...auditDetails });
          } else {
            this.fg.reset(this.defaultAudit);
          }
        },
        error: (err) => this.error$.next(err)
      });
  }

  private initError(): void {
    this.error$.pipe(takeUntil(this.onDestroy$)).subscribe((err) => {
      console.log('Error', err);
      this.toaster.error({ message: 'There was an error processing that request', dataCy: 'siem-general-toast-error' });
    });
  }

  getFormValidationErrors(): string[] {
    const result = [];
    Object.keys(this.fg.controls).forEach((key) => {
      const controlErrors: ValidationErrors = this.fg.get(key).errors;
      if (controlErrors != null) {
        Object.keys(controlErrors).forEach((keyError) => {
          if (keyError === 'pattern') {
            result.push(key + ' must be a valid URI and use https');
          } else if (keyError === 'required') {
            result.push(key + ' is a required field');
          }
        });
      }
    });
    return result;
  }

  protected readonly StatusClass = StatusClass;
}
